<template>
  <div
    ref="modal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="staticBackdropLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-scrollable modal-xl">
      <div class="modal-content" v-if="show && showData">
        <div class="modal-header">
          <span
            class="modal-block-icon"
            v-html="selectedBlock?.type?.icon"
          ></span>
          <!-- A renamed block keeps its type visible underneath: the custom
               name says which one this is, the type says what it does. -->
          <span class="modal-title-stack">
            <h5 class="modal-title" id="staticBackdropLabel">
              {{
                selectedBlock?.value?.block_name || selectedBlock?.type?.name
              }}
            </h5>
            <span
              class="modal-title-type"
              v-if="selectedBlock?.value?.block_name"
            >
              {{ selectedBlock?.type?.name }}
            </span>
          </span>
          <!-- The language decides which version of the text below is being
               edited, so it belongs before the fields, not next to Save. -->
          <div class="lang-pick" v-if="translatable">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path
                d="M18.5 10l4.4 11h-2.155l-1.201-3h-4.09l-1.199 3h-2.154L16.5 10h2zM10 2v2h6v2h-1.968a18.222 18.222 0 0 1-3.62 6.301 14.864 14.864 0 0 0 2.336 1.707l-.751 1.878A17.015 17.015 0 0 1 9 13.725a16.676 16.676 0 0 1-6.201 3.548l-.536-1.929a14.7 14.7 0 0 0 5.327-3.042A18.078 18.078 0 0 1 4.767 8h2.24A16.032 16.032 0 0 0 9 10.877a16.165 16.165 0 0 0 2.91-4.876L2 6V4h6V2h2zm7.5 10.885L16.253 16h2.492L17.5 12.885z"
              />
            </svg>
            <span class="lang-pick-label">Editing in</span>
            <select v-model="language">
              <option
                v-for="lang in languages"
                :key="lang.code"
                :value="lang.code"
              >
                {{ lang.name }}
              </option>
            </select>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            v-on:click="$emit('update:show', false)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path fill="none" d="M0 0h24v24H0z" />
              <path
                d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
              />
            </svg>
          </button>
        </div>
        <div class="modal-body px-5">
          <div class="edit-grid" :class="{ 'has-side': imageSections.length }">
            <div class="edit-main">
              <div
                v-for="section in mainSections"
                :key="section"
                :class="{
                  'field-box': isBoxedField(section),
                  'is-filled': isBoxedField(section) && fieldHasValue(section),
                }"
              >
                <!-- Single-control fields carry their name inside the box: it
                 saves the whole label row and keeps one width for every
                 field, whatever control type sits in it. -->
                <span v-if="isBoxedField(section)" class="field-box-label">
                  {{ section?.name }}
                  <span
                    v-if="seoGapHint(section)"
                    class="seo-flag"
                    :title="seoGapHint(section)"
                    >SEO</span
                  >
                </span>
                <h3 v-else>
                  {{ section?.name }}
                  <span
                    v-if="seoGapHint(section)"
                    class="seo-flag"
                    :title="seoGapHint(section)"
                    >SEO</span
                  >
                </h3>
                <div
                  class="mb-4"
                  v-if="
                    section.type == 'featured-products' ||
                    section.type == 'featured-posts'
                  "
                >
                  <div class="section-label mb-2">How to pick products</div>
                  <div class="option-grid">
                    <!-- Product -->
                    <button
                      class="option-card"
                      :class="{ active: productSelector === 'product' }"
                      @click="productSelector = 'product'"
                    >
                      <span class="option-card-icon">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M15 15m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                          <path d="M18.5 18.5l2.5 2.5" />
                          <path d="M4 6h16" />
                          <path d="M4 12h4" />
                          <path d="M4 18h4" />
                        </svg>
                      </span>
                      <span class="option-card-title">Products</span>
                      <span class="option-card-sub">Pick individual items</span>
                    </button>
                    <!-- Bundles -->
                    <button
                      class="option-card"
                      :class="{ active: productSelector === 'bundle' }"
                      @click="productSelector = 'bundle'"
                    >
                      <span class="option-card-icon">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M13 13v-4a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v2.5"
                          />
                          <path
                            d="M18 8v-3a1 1 0 0 0 -1 -1h-13a1 1 0 0 0 -1 1v12a1 1 0 0 0 1 1h7"
                          />
                          <path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                          <path d="M20.2 20.2l1.8 1.8" />
                          <path d="M16 9h2" />
                        </svg>
                      </span>
                      <span class="option-card-title">Bundles</span>
                      <span class="option-card-sub">Pick product bundles</span>
                    </button>
                    <!-- Filters -->
                    <button
                      class="option-card"
                      :class="{ active: productSelector === 'filter' }"
                      @click="productSelector = 'filter'"
                    >
                      <span class="option-card-icon">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M11.36 20.213l-2.36 .787v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414"
                          />
                          <path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                          <path d="M20.2 20.2l1.8 1.8" />
                        </svg>
                      </span>
                      <span class="option-card-title">Filters</span>
                      <span class="option-card-sub">Auto-select by rules</span>
                    </button>
                    <button
                      v-if="dynamic?.length > 0"
                      class="option-card"
                      :class="{ active: productSelector === 'dynamic' }"
                      @click="productSelector = 'dynamic'"
                    >
                      <span class="option-card-icon">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />
                        </svg>
                      </span>
                      <span class="option-card-title">Dynamic</span>
                      <span class="option-card-sub">From a dynamic list</span>
                    </button>
                  </div>
                  <div
                    v-if="
                      productSelector === 'product' ||
                      productSelector === 'bundle'
                    "
                  >
                    <div
                      class="row custom-input whitebg-input"
                      v-for="p in productList"
                      :key="p.id"
                      v-show="p.id === null"
                    >
                      <div>
                        <div class="mb-3">
                          <label class="field-label mb-1">Add a product</label>
                          <v-select
                            v-model:options="p.products"
                            v-on:search="(s, k) => fetchProducts(s, k, p)"
                            v-model="p.id"
                            :filterable="false"
                            placeholder="Search for products"
                            :reduce="(c) => c.variant_id"
                            :getOptionKey="(c) => c.variant_id"
                            v-on:option:selected="
                              (e) => changeProducts(e.variant_id)
                            "
                            v-on:option:deselected="() => changeProducts(0)"
                          >
                            <template v-slot:no-options="{ search, searching }">
                              <template v-if="searching && search.length > 2">
                                No result for
                                <em>{{ search }}</em
                                >.
                              </template>
                              <span v-else>No result</span>
                            </template>
                            <template v-slot:selected-option="option">
                              <div class="row">
                                <div class="col-auto px-0">
                                  <img
                                    :src="productImage(option)"
                                    v-if="productImage(option)"
                                    class="product-image"
                                    alt=""
                                  />
                                  <BaseIcon
                                    v-else
                                    name="no-image"
                                    class="product-image"
                                  />
                                </div>
                                <div class="col">
                                  <div>
                                    <b>{{ option.name }}</b>
                                  </div>
                                  <div>
                                    <span
                                      v-for="char in option.caracteristics"
                                      :key="char"
                                      class="me-2"
                                    >
                                      {{ char }}
                                    </span>
                                    <span class="me-2">
                                      {{ option.serial }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <!-- A serial alone does not identify a swimsuit;
                               the picture is what the user recognises. -->
                            <template v-slot:option="option">
                              <div class="prod-opt">
                                <img
                                  v-if="productImage(option)"
                                  :src="productImage(option)"
                                  class="prod-opt-img"
                                  alt=""
                                />
                                <span
                                  v-else
                                  class="prod-opt-img is-empty"
                                ></span>
                                <span class="prod-opt-text">
                                  <span class="prod-opt-name">{{
                                    option.name
                                  }}</span>
                                  <span class="prod-opt-meta">
                                    <span v-if="option.serial">{{
                                      option.serial
                                    }}</span>
                                    <span
                                      v-for="char in option.caracteristics"
                                      :key="char"
                                      >{{ char }}</span
                                    >
                                  </span>
                                </span>
                              </div>
                            </template>
                          </v-select>
                        </div>
                      </div>
                    </div>
                    <!-- Chosen products are a list, not a set of dropdowns:
                       once picked there is nothing to re-choose, only to
                       remove or reorder. The order here is the order they
                       are saved in. -->
                    <div class="picked-list" v-if="pickedProducts.length">
                      <div class="section-label mb-2">
                        Chosen products
                        <span class="picked-hint">drag to reorder</span>
                      </div>
                      <draggable
                        :list="productList"
                        item-key="id"
                        handle=".picked-grip"
                        :move="allowPickedMove"
                      >
                        <template #item="{ element }">
                          <div class="picked-row" v-if="element.id !== null">
                            <span class="picked-grip" title="Drag to reorder">
                              <svg
                                viewBox="0 0 24 24"
                                width="15"
                                height="15"
                                fill="currentColor"
                              >
                                <path
                                  d="M9 4h2v2H9V4zm4 0h2v2h-2V4zM9 9h2v2H9V9zm4 0h2v2h-2V9zm-4 5h2v2H9v-2zm4 0h2v2h-2v-2zm-4 5h2v2H9v-2zm4 0h2v2h-2v-2z"
                                />
                              </svg>
                            </span>
                            <img
                              v-if="productImage(productOf(element))"
                              :src="productImage(productOf(element))"
                              class="picked-img"
                              alt=""
                            />
                            <span v-else class="picked-img is-empty"></span>
                            <span class="picked-text">
                              <span class="picked-name">{{
                                productOf(element)?.name ?? "Unknown product"
                              }}</span>
                              <span class="picked-meta">
                                <span v-if="productOf(element)?.serial">{{
                                  productOf(element).serial
                                }}</span>
                                <span
                                  v-for="char in productOf(element)
                                    ?.caracteristics ?? []"
                                  :key="char"
                                  >{{ char }}</span
                                >
                              </span>
                            </span>
                            <button
                              class="picked-remove"
                              title="Remove"
                              v-on:click="removePicked(element)"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                width="15"
                                height="15"
                                fill="currentColor"
                              >
                                <path
                                  d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
                                />
                              </svg>
                            </button>
                          </div>
                        </template>
                      </draggable>
                    </div>
                    <label class="check-row soldout-row">
                      Show sold out
                      <input
                        type="checkbox"
                        :value="true"
                        v-model="settingsData.showSoldOut"
                      />
                    </label>
                  </div>
                  <div class="mt-3" v-if="productSelector === 'dynamic'">
                    <div class="row">
                      <div class="col-12">
                        <label class="field-label mb-1">Product list</label>
                        <v-select
                          v-model="settingsData.dynamic_block"
                          :options="dynamic"
                          placeholder="Select variable"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="filter-panel" v-if="productSelector === 'filter'">
                    <div class="filter-grid">
                      <div class="field field-wide">
                        <span class="field-label">Show</span>
                        <!-- The type is a filter of its own. Two separate
                           category boxes could not say "bundles only": an
                           empty product category means "every product", so
                           products came along whatever the bundle box held. -->
                        <fieldset
                          class="segmented-control"
                          role="radiogroup"
                          aria-label="What to show"
                        >
                          <input
                            type="radio"
                            name="show_types"
                            id="show_both"
                            value="both"
                            v-model="showTypes"
                          />
                          <label for="show_both">Products and bundles</label>
                          <input
                            type="radio"
                            name="show_types"
                            id="show_products"
                            value="product"
                            v-model="showTypes"
                          />
                          <label for="show_products">Products only</label>
                          <input
                            type="radio"
                            name="show_types"
                            id="show_bundles"
                            value="bundle"
                            v-model="showTypes"
                          />
                          <label for="show_bundles">Bundles only</label>
                        </fieldset>
                      </div>
                      <div class="field">
                        <span class="field-label">Product limit</span>
                        <input
                          type="number"
                          v-model="settingsData.limit"
                          placeholder="30"
                        />
                      </div>
                      <div class="field field-wide">
                        <span class="field-label">Product categories</span>
                        <template
                          v-if="
                            section.categories === null ||
                            section.categories === undefined ||
                            section.categories === 1
                          "
                        >
                          <v-select
                            v-if="editorMode['category'] === 'select'"
                            v-model="settingsData.featured_category"
                            v-model:options="categories"
                            label="name"
                            :filter="
                              (s, q) =>
                                s.filter(
                                  (e) => e.name.toLowerCase().indexOf(q) > -1
                                )
                            "
                            multiple
                            placeholder="Every category"
                            :reduce="(c) => c.featured_category"
                          />
                          <v-select
                            v-else-if="editorMode['category'] === 'variables'"
                            v-model="settingsData.var_featured_category"
                            :options="variables"
                            placeholder="Select variable"
                          />
                        </template>
                        <template v-else>
                          <div
                            v-for="i in Array.from(
                              { length: section.categories },
                              (_, i) => i
                            )"
                            :key="i"
                          >
                            <v-select
                              v-if="editorMode['category'] === 'select'"
                              v-model="settingsData.featured_category[i]"
                              v-model:options="categories"
                              label="name"
                              :filter="
                                (s, q) =>
                                  s.filter(
                                    (e) => e.name.toLowerCase().indexOf(q) > -1
                                  )
                              "
                              multiple
                              placeholder="Every category"
                              :reduce="(c) => c.featured_category"
                            />
                            <v-select
                              v-else-if="editorMode['category'] === 'variables'"
                              v-model="settingsData.var_featured_category[i]"
                              :options="variables"
                              placeholder="Select variable"
                            />
                          </div>
                        </template>
                        <div class="mode-switch" v-if="variables?.length > 0">
                          <fieldset
                            class="segmented-control"
                            role="radiogroup"
                            aria-label="Mode"
                          >
                            <input
                              type="radio"
                              name="mode_category"
                              id="mode_val_category"
                              value="select"
                              v-model="editorMode['category']"
                            />
                            <label for="mode_val_category">
                              Select values
                            </label>
                            <input
                              type="radio"
                              name="mode_category"
                              id="mode_vars_category"
                              value="variables"
                              v-model="editorMode['category']"
                            />
                            <label for="mode_vars_category">
                              Use variables
                            </label>
                          </fieldset>
                        </div>
                      </div>
                      <div class="field field-wide">
                        <span class="field-label">Bundle categories</span>
                        <template
                          v-if="
                            section.categories === null ||
                            section.categories === undefined ||
                            section.categories === 1
                          "
                        >
                          <v-select
                            v-if="editorMode['category'] === 'select'"
                            v-model="settingsData.featured_bundle_category"
                            v-model:options="categories"
                            label="name"
                            :filter="
                              (s, q) =>
                                s.filter(
                                  (e) => e.name.toLowerCase().indexOf(q) > -1
                                )
                            "
                            multiple
                            placeholder="Every bundle category"
                            :reduce="(c) => c.featured_category"
                          />
                          <v-select
                            v-else-if="editorMode['category'] === 'variables'"
                            v-model="settingsData.var_featured_bundle_category"
                            :options="variables"
                            placeholder="Select variable"
                          />
                        </template>
                        <template v-else>
                          <div
                            v-for="i in Array.from(
                              { length: section.categories },
                              (_, i) => i
                            )"
                            :key="'bundle-' + i"
                          >
                            <v-select
                              v-if="editorMode['category'] === 'select'"
                              v-model="settingsData.featured_bundle_category[i]"
                              v-model:options="categories"
                              label="name"
                              :filter="
                                (s, q) =>
                                  s.filter(
                                    (e) => e.name.toLowerCase().indexOf(q) > -1
                                  )
                              "
                              multiple
                              placeholder="Every bundle category"
                              :reduce="(c) => c.featured_category"
                            />
                            <v-select
                              v-else-if="editorMode['category'] === 'variables'"
                              v-model="
                                settingsData.var_featured_bundle_category[i]
                              "
                              :options="variables"
                              placeholder="Select variable"
                            />
                          </div>
                        </template>
                      </div>
                    </div>
                    <div class="filter-row-2">
                      <div class="field">
                        <span class="field-label">Product discount</span>
                        <RangeSlider
                          v-model:range="settingsData.discount"
                          :min="0"
                          :max="100"
                          :step="1"
                          :format="(v) => `${v}%`"
                        />
                      </div>
                      <div class="field">
                        <span class="field-label">Sort by</span>
                        <v-select
                          v-model="settingsData.target_sort"
                          label="name"
                          :reduce="(c) => c.id"
                          :options="[
                            { id: 'order', name: 'Order' },
                            { id: 'updated_at', name: 'Updated at' },
                            { id: 'created_at', name: 'Created at' },
                            { id: 'discount', name: 'Discount' },
                            { id: 'product_prices', name: 'Product price' },
                          ]"
                          placeholder="Default order"
                        />
                      </div>
                    </div>
                    <div class="filter-groups">
                      <div class="filter-group">
                        <div class="filter-group-head">
                          <span class="filter-group-title">
                            Tags
                            <span
                              v-if="(settingsData.tags || []).length"
                              class="filter-count"
                            >
                              {{ (settingsData.tags || []).length }}
                              <button
                                class="filter-clear"
                                @click="settingsData.tags = []"
                                title="Remove all"
                              >
                                clear
                              </button>
                            </span>
                          </span>
                          <input
                            type="text"
                            class="filter-search"
                            v-model="searchTerms.tags"
                            placeholder="Search…"
                            aria-label="Search tags"
                          />
                        </div>
                        <div class="filter-check-list">
                          <label
                            v-for="tag in sortedAndFilteredTags"
                            :key="tag.id"
                            class="filter-check"
                            :class="{ selected: isTagSelected(tag.id) }"
                          >
                            <input
                              type="checkbox"
                              :value="tag.id"
                              v-model="settingsData.tags"
                            />
                            <span
                              v-html="
                                highlightMatch(tag.name, searchTerms.tags)
                              "
                            ></span>
                          </label>
                          <div
                            v-if="sortedAndFilteredTags.length === 0"
                            class="filter-empty"
                          >
                            No result.
                          </div>
                        </div>
                      </div>
                      <div
                        v-for="ch in characteristics"
                        :key="ch.id"
                        class="filter-group"
                      >
                        <div class="filter-group-head">
                          <span class="filter-group-title">
                            {{ ch.name }}
                            <span
                              v-if="
                                (settingsData.characteristics[ch.id] || [])
                                  .length
                              "
                              v-show="editorMode[ch.id] === 'select'"
                              class="filter-count"
                            >
                              {{
                                (settingsData.characteristics[ch.id] || [])
                                  .length
                              }}
                              <button
                                class="filter-clear"
                                @click="
                                  settingsData.characteristics[ch.id] = []
                                "
                                title="Remove all"
                              >
                                clear
                              </button>
                            </span>
                          </span>
                          <input
                            v-show="editorMode[ch.id] === 'select'"
                            type="text"
                            class="filter-search"
                            v-model="searchTerms[ch.id]"
                            placeholder="Search…"
                            aria-label="Search values"
                          />
                        </div>
                        <div
                          class="filter-check-list"
                          v-show="editorMode[ch.id] === 'select'"
                        >
                          <label
                            v-for="val in sortedAndFilteredValues(ch)"
                            :key="val.id"
                            class="filter-check"
                            :class="{ selected: isSelected(ch.id, val.id) }"
                          >
                            <input
                              type="checkbox"
                              :value="val.id"
                              v-model="settingsData.characteristics[ch.id]"
                            />
                            <span
                              v-html="
                                highlightMatch(val.value, searchTerms[ch.id])
                              "
                            ></span>
                          </label>
                          <div
                            v-if="sortedAndFilteredValues(ch).length === 0"
                            class="filter-empty"
                          >
                            No result.
                          </div>
                        </div>
                        <div
                          v-if="editorMode[ch.id] === 'variables'"
                          class="mt-2"
                        >
                          <v-select
                            v-model="settingsData.var_characteristics[ch.id]"
                            :options="[
                              `product.characteristics.${ch.id}.id`,
                              ...variables,
                            ]"
                            placeholder="Select variable"
                          />
                        </div>
                        <div class="mode-switch" v-if="variables?.length > 0">
                          <fieldset
                            class="segmented-control"
                            role="radiogroup"
                            aria-label="Mode"
                          >
                            <input
                              type="radio"
                              :name="'mode_' + ch.id"
                              :id="'mode_val_' + ch.id"
                              value="select"
                              v-model="editorMode[ch.id]"
                            />
                            <label :for="'mode_val_' + ch.id">
                              Select values
                            </label>
                            <input
                              type="radio"
                              :name="'mode_' + ch.id"
                              :id="'mode_vars_' + ch.id"
                              value="variables"
                              v-model="editorMode[ch.id]"
                            />
                            <label :for="'mode_vars_' + ch.id">
                              Use variables
                            </label>
                          </fieldset>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Quill inserts its toolbar as a sibling of the element it is
                 given, so the wrapper is what frames toolbar and editor as one
                 control. -->
                <div
                  class="mb-4 rt-field"
                  v-else-if="section.type === 'rich-text' && !refresh"
                >
                  <!-- Function ref, not a string ref: a dynamic string :ref inside
                   v-for does not register in $refs under Vue 3 the way it did
                   under Vue 2, so the old $refs[...][0] lookup was undefined and
                   Quill was handed nothing — which is why the Text editor threw
                   and never opened. -->
                  <div
                    :ref="(el) => setQuillEl(section.key, el)"
                    v-html="
                      section.translate
                        ? typeof settingsData[section.key] === 'object'
                          ? settingsData[section.key][language]
                          : ''
                        : settingsData[section.key]
                    "
                  ></div>
                </div>
                <div class="mb-4" v-else-if="section.type === 'long-text'">
                  <textarea
                    type="text"
                    class="form-control"
                    v-model="settingsData[section.key]"
                  ></textarea>
                </div>
                <div class="mb-4" v-else-if="section.type === 'html'">
                  <CodeMirror v-model="settingsData[section.key]" />
                </div>
                <div class="mb-4" v-else-if="section.type === 'variable'">
                  <label>Product list</label>
                  <v-select
                    v-model="settingsData[section.key].val"
                    :options="dynamic"
                    placeholder="Select variable"
                  />
                </div>
                <div
                  class="mb-4 col-12 row"
                  v-else-if="section.type === 'video'"
                >
                  <div class="col-sm-12">
                    {{ settingsData[selectedVideo]?.name }}
                    <button
                      class="btn btn-primary"
                      v-on:click="
                        selectedVideo = section.key;
                        showVideoModal = true;
                      "
                    >
                      Select or upload video
                    </button>
                  </div>
                </div>
                <div
                  class="mb-4 col-12 row"
                  v-else-if="section.type === 'link'"
                >
                  <div class="col-sm-12">
                    <v-select
                      v-model="settingsData[section.key]"
                      :options="filteredLinks"
                      :reduce="(c) => getLink(c)"
                      v-on:search="(s, k) => onLinkSearch(s, k)"
                      :filterable="false"
                      :getOptionKey="(c) => getLink(c)"
                      :create-option="
                        (name) => ({ target_url: name, name: name })
                      "
                      placeholder=""
                      label="name"
                      clearable
                      taggable
                    >
                      <template v-slot:no-options="{ search, searching }">
                        <template v-if="searching && search.length > 2">
                          No item found for <em>{{ search }}</em
                          >.
                        </template>
                        <span v-else> Start typing to search</span>
                      </template>
                      <template v-slot:selected-option="option">
                        <span v-if="typeof option === 'string'">{{
                          option
                        }}</span>
                        <span v-else>{{ option.name }}</span>
                        <span
                          class="badge bg-primary ms-2"
                          v-if="option.featured_category"
                        >
                          Category
                        </span>
                        <span
                          class="badge bg-yellow ms-2"
                          v-if="option.target_page_id"
                        >
                          Page
                        </span>
                        <span
                          class="badge bg-green ms-2"
                          v-if="option.variant_id"
                        >
                          Product
                        </span>
                      </template>
                      <template v-slot:option="option">
                        <span v-if="typeof option === 'string'">{{
                          option
                        }}</span>
                        <span v-else>{{ option.name }}</span>
                        <span
                          class="badge bg-primary ms-2"
                          v-if="option.featured_category"
                        >
                          Category
                        </span>
                        <span
                          class="badge bg-yellow ms-2"
                          v-if="option.target_page_id"
                        >
                          Page
                        </span>
                        <span
                          class="badge bg-green ms-2"
                          v-if="option.variant_id"
                        >
                          Product
                        </span>
                      </template>
                    </v-select>
                  </div>
                </div>
                <div class="mb-4" v-else-if="section.type === 'enum'">
                  <div class="input-group">
                    <select
                      :value="getValue(section)"
                      @change="setValue(section, $event.target.value)"
                    >
                      <option
                        v-for="(c, i) in section.values"
                        :key="i"
                        :value="i"
                      >
                        {{ c }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mb-4" v-else>
                  <input
                    type="text"
                    :value="getValue(section)"
                    @input="setValue(section, $event.target.value)"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
            <div class="edit-side" v-if="imageSections.length">
              <div v-for="section in imageSections" :key="section">
                <div class="img-block">
                  <div class="col-12">
                    <input
                      type="file"
                      :ref="'file_' + btoa(section.key)"
                      :id="'file_' + btoa(section.key)"
                      v-on:change="
                        upload(section.key, section?.image ?? 'cover')
                      "
                      style="display: none"
                    />
                    <div class="img-row">
                      <div class="img-label">
                        <span class="img-label-icon">
                          <!-- Phone for the tall variant, monitor for the wide one,
              so the shape is readable before the label is. -->
                          <svg
                            v-if="imageOrientation(section) === 'portrait'"
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path
                              d="M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm1 2v16h8V4H8zm2 14h4v1h-4v-1z"
                            />
                          </svg>
                          <svg
                            v-else-if="
                              imageOrientation(section) === 'landscape'
                            "
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path
                              d="M3 3h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-7v2h4v2H6v-2h4v-2H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v10h16V5H4z"
                            />
                          </svg>
                          <svg
                            v-else
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path
                              d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zM8 7a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"
                            />
                          </svg>
                        </span>
                        <span class="img-label-text">
                          <span class="img-label-name">
                            {{ section?.name }}
                            <span
                              v-if="seoGapHint(section)"
                              class="seo-flag"
                              :title="seoGapHint(section)"
                              >SEO</span
                            >
                          </span>
                          <span
                            class="img-label-hint"
                            v-if="imageOrientation(section)"
                          >
                            {{
                              imageOrientation(section) === "portrait"
                                ? "Tall — shown on phones"
                                : "Wide — shown on larger screens"
                            }}
                          </span>
                        </span>
                      </div>
                      <!-- The field is the drop target and the button: drag a file
                   onto it, or click to open the library. -->
                      <div class="img-field">
                        <button
                          type="button"
                          class="img-drop"
                          :class="[
                            'is-' + (imageOrientation(section) || 'default'),
                            {
                              'is-empty': !imageSrc(section.key),
                              'is-dragging': fieldDragKey === section.key,
                            },
                          ]"
                          v-on:click="openPicker(section.key)"
                          v-on:dragover.prevent="fieldDragKey = section.key"
                          v-on:dragleave="fieldDragKey = null"
                          v-on:drop.prevent="onFieldDrop($event, section.key)"
                        >
                          <img
                            v-if="imageSrc(section.key)"
                            :src="imageThumb(section.key)"
                            v-on:error="onFieldThumbError($event, section.key)"
                            alt=""
                          />
                          <span class="img-drop-hint">
                            <svg
                              viewBox="0 0 24 24"
                              width="19"
                              height="19"
                              fill="currentColor"
                            >
                              <path
                                d="M12 12.586l4.243 4.242-1.415 1.415L13 16.415V22h-2v-5.587l-1.828 1.83-1.415-1.415L12 12.586zM12 2a7.001 7.001 0 0 1 6.954 6.194A5.5 5.5 0 0 1 18 18.978v-2.014a3.5 3.5 0 1 0-1.5-6.664A5 5 0 1 0 7 9c0 .34.034.672.1.994A3.5 3.5 0 0 0 6 16.964v2.014a5.5 5.5 0 0 1-.954-10.784A7.001 7.001 0 0 1 12 2z"
                              />
                            </svg>
                            <span class="img-drop-title">Drop an image</span>
                            <span class="img-drop-sub">or click to browse</span>
                          </span>
                        </button>
                        <button
                          v-if="imageSrc(section.key)"
                          type="button"
                          class="img-clear"
                          title="Remove image"
                          v-on:click.stop="settingsData[section.key] = null"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            fill="currentColor"
                          >
                            <path
                              d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer custom-input">
          <div class="row w-100">
            <div class="col"></div>
            <div class="col-auto">
              <button class="btn btn-primary" v-on:click="save">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- One dialog for the whole job: search the library, drop a file on it,
       or upload one. It sits above the content modal rather than replacing it,
       so the field you are filling stays visible behind. -->
  <!-- A file dropped next to the dialog rather than on it used to be opened
       by the browser, which navigated away from the editor and took the
       unsaved block with it. Swallowed here instead. -->
  <div
    class="picker-backdrop"
    v-if="picker.open"
    v-on:click.self="closePicker"
    v-on:dragover.prevent
    v-on:drop.prevent="picker.dragging = false"
  >
    <div class="picker-panel" v-on:dragover.prevent="picker.dragging = true">
      <div class="picker-head">
        <h5>Select image</h5>
        <span class="picker-count" v-if="pickerResults.length">
          {{ pickerCountLabel }}
        </span>
        <button type="button" class="picker-close" v-on:click="closePicker">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path
              d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
            />
          </svg>
        </button>
      </div>
      <div class="picker-tools">
        <div class="picker-searchwrap">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path
              d="M18.031 16.617l4.283 4.282-1.415 1.415-4.282-4.283A8.96 8.96 0 0 1 11 20c-4.968 0-9-4.032-9-9s4.032-9 9-9 9 4.032 9 9a8.96 8.96 0 0 1-1.969 5.617zm-2.006-.742A6.977 6.977 0 0 0 18 11c0-3.868-3.133-7-7-7-3.868 0-7 3.132-7 7 0 3.867 3.132 7 7 7a6.977 6.977 0 0 0 4.875-1.975l.15-.15z"
            />
          </svg>
          <input
            class="picker-search"
            type="text"
            v-model="picker.q"
            placeholder="Search media…"
            v-on:input="loadPickerImages"
          />
        </div>
        <input
          type="file"
          id="picker-file"
          accept="image/*"
          style="display: none"
          v-on:change="onPickerFile"
        />
        <button
          type="button"
          class="picker-upload"
          v-on:click="pickerUploadClick"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path
              d="M12 12.586l4.243 4.242-1.415 1.415L13 16.415V22h-2v-5.587l-1.828 1.83-1.415-1.415L12 12.586zM12 2a7.001 7.001 0 0 1 6.954 6.194A5.5 5.5 0 0 1 18 18.978v-2.014a3.5 3.5 0 1 0-1.5-6.664A5 5 0 1 0 7 9c0 .34.034.672.1.994A3.5 3.5 0 0 0 6 16.964v2.014a5.5 5.5 0 0 1-.954-10.784A7.001 7.001 0 0 1 12 2z"
            />
          </svg>
          Upload
        </button>
      </div>
      <p class="picker-hint">
        Drop a file anywhere on this dialog to upload · JPG, PNG, WebP, GIF ·
        max 120 MB
      </p>
      <!-- Whether a picture is deletable is the backend's answer; the trash
           only appears on the ones the site does not use. -->
      <p class="picker-scan" v-if="usage.state === 'loading'">
        Checking which images the site uses…
      </p>
      <p class="picker-scan is-warn" v-else-if="usage.state === 'failed'">
        The site could not be asked what it uses, so deleting is off. Reopen the
        dialog to try again.
      </p>
      <div class="picker-note" v-if="pickerNotice">{{ pickerNotice }}</div>
      <div class="picker-error" v-if="uploadError">{{ uploadError }}</div>
      <div class="picker-progress" v-if="uploadProccess">
        <span :style="`width: ${uploadPercent}%`"></span>
      </div>
      <div class="picker-body">
        <div class="picker-loading" v-if="pickerLoading">Loading media…</div>
        <div class="picker-none" v-else-if="pickerResults.length === 0">
          Nothing here yet. Drop a file on this dialog or use Upload.
        </div>
        <!-- The library runs to a few hundred images and each thumbnail is a
             few hundred KB, so the whole set at once made the dialog crawl. -->
        <div class="picker-grid" v-else>
          <div
            class="picker-card"
            :class="{ 'is-current': isCurrentImage(img) }"
            v-for="img in pickerVisible"
            :key="img.id ?? img.path"
          >
            <!-- The picture is shown whole rather than cropped to a tile:
                 two crops of the same shoot are otherwise impossible to
                 tell apart at thumbnail size. -->
            <button
              type="button"
              class="picker-pick"
              title="Use this image"
              v-on:click="choosePickerImage(img)"
            >
              <img
                :src="pickerThumb(img)"
                loading="lazy"
                decoding="async"
                alt=""
                v-on:error="onThumbError(img)"
              />
            </button>
            <span class="picker-badge" v-if="isCurrentImage(img)"
              >In use here</span
            >
            <div class="picker-acts">
              <button
                type="button"
                class="picker-act"
                title="View full size"
                v-on:click="preview = img"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="currentColor"
                >
                  <path
                    d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.819-9C2.121 6.88 6.608 3 12 3zm0 16a9.005 9.005 0 0 0 8.777-7 9.005 9.005 0 0 0-17.554 0A9.005 9.005 0 0 0 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                  />
                </svg>
              </button>
              <button
                v-if="canDeleteImage(img)"
                type="button"
                class="picker-act is-danger"
                title="Delete image"
                v-on:click="askDeleteImage(img)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="currentColor"
                >
                  <path
                    d="M17 6h4v2h-2v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8H3V6h4V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm0 2H7v12h10V8zm-7 2h2v8h-2v-8zm4 0h2v8h-2v-8zM9 4v2h6V4H9z"
                  />
                </svg>
              </button>
            </div>
            <span class="picker-card-foot">
              <span class="picker-card-name">{{ img.name }}</span>
              <span
                class="picker-card-used"
                v-if="usedPlaces(img).length"
                :title="'In use: ' + usedPlaces(img).join(', ')"
              >
                in use
              </span>
            </span>
          </div>
        </div>
      </div>
      <div class="picker-more" v-if="pickerResults.length > pickerLimit">
        <button type="button" v-on:click="pickerLimit += 40">
          Load more ({{ pickerResults.length - pickerLimit }} left)
        </button>
      </div>
      <!-- While a file is over the dialog this covers it, so the drop lands
           on one element and dragleave is reported once. -->
      <div
        class="picker-dropmask"
        v-if="picker.dragging"
        v-on:dragover.prevent
        v-on:dragleave="picker.dragging = false"
        v-on:drop.prevent="onPickerDrop"
      >
        Drop to upload
      </div>
      <!-- Whether an image is still on the site is the backend's verdict: if
           it reports usage, the card only says so. -->
      <div class="picker-ask" v-if="askDelete">
        <div class="picker-ask-card">
          <template v-if="askDelete.usage.length">
            <h6>This image is in use</h6>
            <p>
              <strong>{{ askDelete.img.name }}</strong> is on the site, so it
              cannot be deleted. It is used here:
            </p>
            <ul class="picker-ask-list">
              <li v-for="(place, n) in askDelete.usage" :key="n">
                {{ place }}
              </li>
            </ul>
            <div class="picker-ask-acts">
              <button type="button" v-on:click="askDelete = null">Close</button>
            </div>
          </template>
          <template v-else>
            <h6>Delete this image?</h6>
            <p>
              <strong>{{ askDelete.img.name }}</strong> is not used anywhere on
              the site.
            </p>
            <p v-if="askDelete.inField">
              It is the picture in the field you are filling, and that field is
              not saved yet: deleting empties it.
            </p>
            <p>
              It will be deleted from the media library and from the server.
              This cannot be undone.
            </p>
            <div class="picker-ask-acts">
              <button
                type="button"
                :disabled="askDelete.deleting"
                v-on:click="askDelete = null"
              >
                Cancel
              </button>
              <button
                type="button"
                class="is-danger"
                :disabled="askDelete.deleting"
                v-on:click="confirmDeleteImage"
              >
                {{ askDelete.deleting ? "Deleting…" : "Delete" }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
  <!-- Full size, because a thumbnail is not enough to tell two crops apart. -->
  <div class="picker-lightbox" v-if="preview" v-on:click.self="preview = null">
    <div class="picker-lightbox-bar">
      <span>{{ preview.name }}</span>
      <button type="button" v-on:click="preview = null">Close</button>
    </div>
    <img :src="pickerFull(preview)" alt="" />
  </div>
  <div class="filter-ask" v-if="askClearFilters">
    <div class="picker-ask-card">
      <h6>This block is filled by filters</h6>
      <p>
        Categories, tags or characteristics are set on this block. Products and
        bundles added by search replace that filtered list when saved.
      </p>
      <p class="picker-ask-warn">Want to clear the previous filtered data?</p>
      <div class="picker-ask-acts">
        <button type="button" v-on:click="askClearFilters = false">Keep</button>
        <button
          type="button"
          class="is-danger"
          v-on:click="confirmClearFilters"
        >
          Clear filtered data
        </button>
      </div>
    </div>
  </div>
  <VideoUpload
    v-model:show="showVideoModal"
    v-on:select="(video) => (settingsData[selectedVideo] = video)"
  />
</template>

<script>
import http from "../../modules/http";
import tenant from "../../modules/tenant";
import VideoUpload from "./VideoUpload";
import draggable from "vuedraggable";
import { useStore } from "vuex";
import CodeMirror from "../CodeMirror.vue";
import RangeSlider from "../RangeSlider.vue";
import pica from "pica";
import { blockSeoGaps } from "../../modules/seo-gaps";
import { smallVariant } from "../../modules/media-size";
import { recordProblem } from "../../modules/problem-log";
const bootstrap = require("bootstrap");

const picaInstance = pica();

// Whether a filter setting actually selects anything: empty arrays, empty
// variable placeholders and untouched characteristic maps count as unset.
const hasSettingValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (Array.isArray(item)) {
        return item.length > 0;
      }
      if (item && typeof item === "object") {
        return Boolean(item.val);
      }
      return Boolean(item);
    });
  }
  if (typeof value === "object") {
    if (value.type === "variable") {
      return Boolean(value.val);
    }
    return Object.values(value).some((item) =>
      Array.isArray(item) ? item.length > 0 : Boolean(item)
    );
  }
  return Boolean(value);
};

export default {
  name: "EditContent",
  components: { RangeSlider, CodeMirror, VideoUpload, draggable },
  props: {
    show: Boolean,
    block: Object,
    settings: Object,
    elements: Object,
    variables: Array,
    dynamic: Array,
    items: Object,
    values: Object,
    languages: Array,
    // Which language the editor itself is showing — the modal opens on that
    // one rather than on whichever language happens to be first in the list.
    pageLanguage: String,
    name: String,
    i: Number,
  },
  emits: [
    "update:show",
    "update:values",
    "addBlock",
    "update:block",
    "update:settings",
  ],
  data() {
    return {
      store: useStore(),
      searchTerms: {},
      // Which values were already ticked when the box was opened. The lists
      // put ticked values on top, but they must not re-sort while someone is
      // clicking through them: a value that jumps out from under the cursor
      // gets clicked a second time and appears to clear itself.
      pinnedFilterOrder: {},
      editorMode: {
        category: "select",
      },
      selectedBlock: {},
      valuesData: {},
      settingsData: {},
      myModal: undefined,
      selectedVideo: null,
      quill: {},
      showData: false,
      language: undefined,
      links: [],
      fileEditType: "",
      // One picker serves every image field; key says which field it is
      // filling, so the field itself needs no state of its own.
      picker: { open: false, key: null, type: 3, q: "", dragging: false },
      pickerResults: [],
      pickerLimit: 40,
      pickerLoading: false,
      pickerNotice: "",
      // A file just uploaded is held here until the library lists it, so it
      // cannot fall off the grid while the media server is still catching up.
      pickerPinned: [],
      // Which thumbnail size a picture is being shown at: not every variant
      // exists for every image, so a 404 steps up to the next size.
      thumbStep: {},
      preview: null,
      askDelete: null,
      askClearFilters: false,
      filtersWarned: false,
      // Whether the site uses each picture, as the backend reports it:
      // { [image name]: [place, …] }. This decides the trash button.
      usageByName: {},
      usage: { state: "idle" },
      uploadError: "",
      fieldDragKey: null,
      productList: [{ id: null, products: [] }],
      q: "",
      productSelector: undefined,
      categories: [],
      characteristics: [],
      refresh: false,
      // rich-text editor host elements, collected via function refs (see the
      // template) because dynamic string refs in v-for are unreliable in Vue 3
      quillEls: {},
      translatable: false,
      uploadPercent: 0,
      uploadProccess: false,
      showVideoModal: false,
      linkSearch: "",
      linkProducts: { products: [] },
    };
  },
  computed: {
    // Text fields and images are laid out in two columns, so the sections are
    // split here rather than branched on inside a single loop.
    visibleSections() {
      return this.elements.filter((e) => e.type !== "array");
    },
    pickerVisible() {
      return this.pickerResults.slice(0, this.pickerLimit);
    },
    // One control over settingsData.types, which is the list the site is asked
    // for ("product", "bundle" or both).
    showTypes: {
      get() {
        const types = this.settingsData?.types;
        if (!Array.isArray(types) || types.length !== 1) {
          return "both";
        }
        return types[0] === "bundle" ? "bundle" : "product";
      },
      set(value) {
        this.settingsData.types =
          value === "both" ? ["product", "bundle"] : [value];
      },
    },
    pickerCountLabel() {
      const all = this.pickerResults.length;
      if (this.pickerVisible.length < all) {
        return `${this.pickerVisible.length} of ${all} shown`;
      }
      return all === 1 ? "1 image" : `${all} images`;
    },
    // The image the field being filled already holds, so the grid can point
    // it out instead of leaving the user to recognise it.
    pickerCurrent() {
      const value = this.settingsData?.[this.picker.key];
      return value && typeof value === "object" ? value : null;
    },
    pickedProducts() {
      return this.productList.filter((p) => p.id !== null);
    },
    // The block still runs on filter-based selection when any of the keys the
    // filter or dynamic modes write to holds data.
    hasActiveFilters() {
      const s = this.settingsData;
      return (
        hasSettingValue(s.featured_category) ||
        hasSettingValue(s.var_featured_category) ||
        hasSettingValue(s.featured_bundle_category) ||
        hasSettingValue(s.var_featured_bundle_category) ||
        hasSettingValue(s.tags) ||
        hasSettingValue(s.characteristics) ||
        hasSettingValue(s.var_characteristics) ||
        Boolean(s.dynamic_block)
      );
    },
    // The fields the block list flags as SEO gaps, keyed so each field can say
    // so next to itself. The list badge told you a block was missing
    // something; it took opening the block and guessing to find out which
    // field that was.
    seoGaps() {
      const byKey = {};
      for (const gap of blockSeoGaps(
        this.elements,
        this.settingsData,
        this.language
      )) {
        byKey[gap.key] = gap;
      }
      return byKey;
    },
    // filterCharacteristics - For Claude: Never filter Characteristics - not even  "Model". There is a setting for this on backend
    imageSections() {
      return this.visibleSections.filter((e) => e.type === "image");
    },
    mainSections() {
      return this.visibleSections.filter((e) => e.type !== "image");
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    // Which variants the media server actually generated, per image type.
    // Files live at <path>/<size>-<name>.webp — <path> already ends with
    // <name>, and no unsized original is written.
    imageSizeTable() {
      return {
        0: { name: "PRODUCT", sizes: [262, 582, 720, 1440] },
        2: {
          name: "CATEGORY",
          sizes: [360, 480, 540, 768, 960, 1280, 1440, 1600],
        },
        3: {
          name: "COVER",
          sizes: [360, 480, 537, 540, 768, 960, 1280, 1440, 1600],
        },
        4: { name: "COLOR", sizes: [32, 64, 128] },
        5: { name: "MENU", sizes: [32, 64, 128, 262, 360] },
      };
    },
    tags() {
      return this.store.state.user.tags.products;
    },
    filteredLinks() {
      let search = this.linkSearch.trim().toLowerCase();

      if (!search) {
        return [...this.links, ...this.linkProducts.products];
      }
      return [
        ...this.links.filter((l) => l.name.toLowerCase().indexOf(search) > -1),
        ...this.linkProducts.products,
      ];
    },
    sortedAndFilteredTags() {
      const term = (this.searchTerms.tags || "")
        .toString()
        .toLowerCase()
        .trim();
      let values = (this.tags || []).filter((tag) => {
        if (!term) return true;
        return String(tag.name).toLowerCase().includes(term);
      });
      return this.pinnedFirst("tags", values, this.settingsData.tags);
    },
  },
  mounted() {
    this.myModal = new bootstrap.Modal(this.$refs.modal, {
      backdrop: "static",
    });
    if (this.show) {
      // mounted on demand (v-if): the show watcher never fires because show
      // is already true, so run the full open sequence here
      this.openModal();
    }
    // Nothing in the else branch on purpose. Hiding a modal that has never
    // been shown throws inside bootstrap ("reading 'style'" on a backdrop it
    // never created) and leaves the hide half-done — which is how the page
    // ended up with a stuck backdrop and body.modal-open, i.e. a screen that
    // looks fine and ignores every click.
  },
  unmounted() {
    // the component is mounted on demand — clean up bootstrap leftovers
    if (this.myModal) {
      this.myModal.dispose();
      this.myModal = undefined;
    }
    document
      .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
      .forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
  },
  watch: {
    block() {
      this.selectedBlock = this.block;
    },
    settings() {
      //this.settingsData = this.settings;
      this.settingsData = Object.assign({}, this.settings);

      for (const section of this.elements) {
        if (section?.translate) {
          if (typeof this.settingsData[section.key] !== "object") {
            this.settingsData[section.key] = {};
          }
        }
        if (
          section.type === "variable" &&
          typeof this.settingsData[section.key] !== "object"
        ) {
          this.settingsData[section.key] = { type: "variable", val: "" };
        }
      }
    },
    productSelector() {
      if (this.productSelector === "filter") {
        if (typeof this.settingsData.featured_category !== "object") {
          this.settingsData.featured_category = [];
        }
        if (typeof this.settingsData.featured_bundle_category !== "object") {
          this.settingsData.featured_bundle_category = [];
        }
        if (typeof this.settingsData.tags !== "object") {
          this.settingsData.tags = [];
        }
        if (typeof this.settingsData.characteristics !== "object") {
          this.settingsData.characteristics = {};
        }
        if (typeof this.settingsData.var_characteristics !== "object") {
          this.settingsData.var_characteristics = {};
        }
        for (const characteristics of this.characteristics) {
          if (
            !Array.isArray(
              this.settingsData.characteristics[characteristics.id]
            )
          ) {
            this.settingsData.characteristics[characteristics.id] = [];
          }
        }
        // Re-opening the filters pins the order afresh, so the values in use
        // are on top again — the groups only load once per modal.
        this.pinFilterOrder();
      }
    },
    languages() {
      if (this.language === undefined && this.languages.length > 0) {
        this.language = this.languages[0].code;
      }
    },
    values: {
      deep: true,
      handler() {
        this.valuesData = Object.assign({}, this.values);
      },
    },
    show() {
      if (this.show) {
        this.openModal();
      } else {
        this.showData = false;
        this.safeHide();
      }
    },
    language(value, old) {
      for (const section of this.elements) {
        if (section?.type === "rich-text" && section?.translate) {
          const q = this.quill[section.key];
          if (q) {
            this.settingsData[section.key][old] = q.root.innerHTML;
          }
        }
      }
      this.refresh = true;
      setTimeout(() => {
        this.refresh = false;
        setTimeout(() => {
          for (const section of this.elements) {
            if (section?.type === "rich-text") {
              this.loadQuill(section);
            }
          }
        }, 200);
      }, 200);
    },
  },
  methods: {
    // An image field holds either a plain URL string or an object with .src
    imageSrc(key) {
      const value = this.settingsData?.[key];
      if (!value) {
        return null;
      }
      return typeof value === "string" ? value : value.src ?? null;
    },
    // What the field actually draws. The preview box is 90-200px wide and the
    // stored picture is usually the 3000px original, so a small render is
    // asked for instead — several megabytes per field otherwise, on every
    // image field in the block.
    imageThumb(key) {
      const src = this.imageSrc(key);
      return src ? smallVariant(src) : src;
    },
    // The small render may not exist for every picture; fall back rather than
    // show a broken frame.
    onFieldThumbError(event, key) {
      const original = this.imageSrc(key);
      if (original && event?.target && event.target.src !== original) {
        event.target.src = original;
      }
    },
    // Which shape the placeholder should take. Read from the field's own key
    // and label rather than a fixed list, so a template that names its fields
    // differently still gets the right frame — and an unrecognised one just
    // falls back to a neutral box instead of showing a misleading shape.
    // Only fields that are a single control get the boxed treatment. The
    // multi-control sections (product pickers, rich text, media) have their
    // own internal layout and a box around them would fight it.
    // Drives the floating label: empty fields show the name large and faint in
    // the middle of the box, filled ones shrink it to the top so the name is
    // still readable next to the value.
    // The entry holds only a variant_id; the object with the picture and the
    // serial lives in the list the search filled in.
    // The search returns a path relative to the media server, not a URL. Same
    // helper as BlockEditor.productImage — rendering the raw value is why the
    // pictures came through as broken images.
    // ---- Product filters ----
    // The two category lists are saved as the user picked them, one for
    // products and one for bundles. The type list says which of the two the
    // site is asked to render.
    // ---- Media picker ----
    // Search, browse and upload were three separate inline modes reached by
    // three separate buttons. They are one dialog now: the field is the drop
    // target, and clicking it opens the library.
    openPicker(key, type = 3) {
      this.picker.open = true;
      this.picker.key = key;
      this.picker.type = type;
      this.picker.q = "";
      this.uploadError = "";
      this.pickerNotice = "";
      this.pickerPinned = [];
      this.preview = null;
      this.askDelete = null;
      this.loadPickerImages();
      this.loadImageUsage();
    },
    closePicker() {
      this.picker.open = false;
      this.picker.dragging = false;
      this.preview = null;
      this.askDelete = null;
    },
    loadPickerImages(keepNotice = false) {
      const q = this.picker.q.trim();
      this.pickerLimit = 40;
      this.pickerLoading = true;
      if (!keepNotice) {
        this.pickerNotice = "";
      }
      const request =
        q.length > 2
          ? http.fetch("/images/search", { q })
          : http.fetch("/images?type=" + this.picker.type);
      request
        .then((data) => {
          this.pickerResults = this.newestFirst(
            Array.isArray(data) ? data : []
          );
          this.pickerLoading = false;
        })
        .catch(() => {
          this.pickerResults = this.newestFirst([]);
          this.pickerLoading = false;
        });
    },
    // The API hands the library back oldest first, which put a file just
    // uploaded on the last page of the grid — where nobody looked for it.
    // Ordered by id, not by date: not every record carries a timestamp, and
    // sorting on one put the untimed images below everything else.
    newestFirst(list) {
      const stamp = (img) => {
        const t = Date.parse(img?.created_at ?? img?.updated_at ?? "");
        return Number.isNaN(t) ? 0 : t;
      };
      const sorted = [...list].sort(
        (a, b) => (b?.id ?? 0) - (a?.id ?? 0) || stamp(b) - stamp(a)
      );
      const pinned = this.pickerPinned.filter(
        (p) => !sorted.some((img) => img.path === p.path)
      );
      return [...pinned, ...sorted];
    },
    pickerThumb(img) {
      if (img?.remote) {
        return img.path;
      }
      const sizes = this.imageSizeTable[img?.type]?.sizes;
      if (!sizes || !img?.path) {
        return "";
      }
      const step = Math.min(this.thumbStep[img.id] ?? 0, sizes.length - 1);
      return `${this.mediaServer}/${img.path}/${sizes[step]}-${img.name}.webp`;
    },
    // Some images have no small variant on the media server, and those came
    // through as broken pictures. Step up a size until one answers.
    onThumbError(img) {
      const sizes = this.imageSizeTable[img?.type]?.sizes;
      if (!sizes || img?.id === undefined) {
        return;
      }
      const next = (this.thumbStep[img.id] ?? 0) + 1;
      if (next < sizes.length) {
        this.thumbStep[img.id] = next;
      }
    },
    pickerFull(img) {
      if (img?.remote) {
        return img.path;
      }
      const sizes = this.imageSizeTable[img?.type]?.sizes;
      if (!sizes || !img?.path) {
        return "";
      }
      return `${this.mediaServer}/${img.path}/${sizes[sizes.length - 1]}-${
        img.name
      }.webp`;
    },
    isCurrentImage(img) {
      const current = this.pickerCurrent;
      if (!current || !img) {
        return false;
      }
      if (current.id !== undefined && img.id !== undefined) {
        return current.id === img.id;
      }
      return !!img.path && !!current.src && current.src.includes(img.path);
    },
    choosePickerImage(img) {
      this.settingsData[this.picker.key] = this.getImageLinks(img);
      this.closePicker();
    },
    // ---- Deleting from the library ----
    // Whether a picture can go is the API's answer, not a local guess: the
    // backend scans every page and every record that points at an image, and
    // refuses the delete itself if something turns up. The editor only reads
    // the map once per dialog.
    loadImageUsage() {
      this.usage.state = "loading";
      http
        .fetch("/images/usage")
        .then((data) => {
          this.usageByName = data?.usage ?? {};
          this.usage.state = "ready";
        })
        .catch((e) => {
          console.warn("Media picker: image usage could not be read", e);
          this.usageByName = {};
          this.usage.state = "failed";
        });
    },
    usedPlaces(img) {
      return (img?.name && this.usageByName[img.name]) || [];
    },
    // Only what is on a page counts as being on the site. The block open in
    // front of the dialog is NOT on the site — nothing there is saved yet — so
    // it is reported separately and does not stand in the way of a delete.
    usedInOpenBlock(img) {
      const needle = img?.path ? img.path + "/" : null;
      if (!needle) {
        return false;
      }
      const open =
        JSON.stringify(this.settingsData ?? {}) +
        JSON.stringify(this.valuesData ?? {});
      return open.includes(needle);
    },
    // The trash only appears once the answer is known and the answer is "the
    // site does not use this". A picture the site uses says so instead.
    canDeleteImage(img) {
      return (
        !!img?.id &&
        !img.remote &&
        this.usage.state === "ready" &&
        this.usedPlaces(img).length === 0
      );
    },
    // The usage map is already in hand when the trash is clickable, so the
    // card can show its verdict at once.
    askDeleteImage(img) {
      this.askDelete = {
        img,
        usage: this.usedPlaces(img),
        inField: this.usedInOpenBlock(img) || this.isCurrentImage(img),
        deleting: false,
      };
    },
    confirmDeleteImage() {
      const img = this.askDelete?.img;
      if (!img) {
        return;
      }
      this.askDelete.deleting = true;
      http
        .fetch("/images/delete", { image: img.id })
        .then((data) => {
          this.pickerResults = this.pickerResults.filter(
            (i) => i.id !== img.id
          );
          this.pickerPinned = this.pickerPinned.filter(
            (i) => i.path !== img.path
          );
          delete this.usageByName[img.name];
          // The field cannot go on pointing at a picture that no longer
          // exists — it would render as a hole on the page.
          if (this.isCurrentImage(img)) {
            this.settingsData[this.picker.key] = null;
          }
          this.pickerNotice = `${img.name} deleted.`;
          this.askDelete = null;
          // The record is gone; the file itself only goes when the API says
          // nothing else points at it any more.
          if (data?.need_delete) {
            const request = new XMLHttpRequest();
            const formData = new FormData();
            formData.append("token", data.token);
            formData.append("path", data.image.path);
            request.open(
              "POST",
              process.env.VUE_APP_MEDIA_API + "/images/delete"
            );
            request.send(formData);
          }
        })
        .catch((err) => {
          // The backend re-checks at delete time; if something turned up since
          // the dialog was filled, the card turns into the "in use" list.
          if (this.askDelete) {
            this.askDelete.deleting = false;
            if (Array.isArray(err?.usage) && err.usage.length > 0) {
              this.askDelete.usage = err.usage;
              return;
            }
          }
          this.uploadError = "Could not delete that image. Please try again.";
        });
    },
    pickerUploadClick() {
      document.getElementById("picker-file")?.click();
    },
    onPickerFile(event) {
      const file = event.target?.files?.[0];
      this.uploadFile(file, this.picker.key, this.picker.type);
      if (event.target) {
        event.target.value = "";
      }
    },
    onPickerDrop(event) {
      this.picker.dragging = false;
      this.pickerNotice = "";
      this.uploadFile(
        event.dataTransfer?.files?.[0],
        this.picker.key,
        this.picker.type
      );
    },
    // Dropping straight onto a field skips the dialog entirely.
    onFieldDrop(event, key) {
      this.fieldDragKey = null;
      this.uploadError = "";
      this.picker.key = key;
      this.uploadFile(event.dataTransfer?.files?.[0], key, 3);
    },
    productImage(product) {
      const image = product?.image;
      if (!image) {
        return "";
      }
      return String(image).startsWith("http")
        ? image
        : `${tenant.getAppMediaUrl()}/${image}`;
    },
    productOf(entry) {
      return (
        entry?.products?.find((p) => p.variant_id === entry.id) ??
        entry?.products?.[0] ??
        null
      );
    },
    removePicked(entry) {
      const i = this.productList.indexOf(entry);
      if (i > -1) {
        this.productList.splice(i, 1);
      }
      if (!this.productList.some((p) => p.id === null)) {
        this.productList.unshift({ id: null, products: [] });
      }
    },
    // The empty search row is part of the same list, so keep it out of the
    // drop zone — dragging a card past it would reorder nothing visible.
    allowPickedMove(event) {
      return event.related?.__draggable_context?.element?.id !== null;
    },
    fieldHasValue(section) {
      const value = this.getValue(section);
      if (value === null || value === undefined || value === "") {
        return false;
      }
      return typeof value === "object" ? Object.keys(value).length > 0 : true;
    },
    // Why this field is flagged, in words the person filling it in can act on.
    // Null when there is nothing wrong with it.
    seoGapHint(section) {
      const gap = this.seoGaps[section?.key];
      if (!gap) {
        return null;
      }
      return gap.isAlt
        ? "Search engines and screen readers read this instead of the picture"
        : "This block shows a picture here and none is set";
    },
    // Closing must never leave the page unusable: if bootstrap throws
    // half-way through hiding, the backdrop and body.modal-open stay behind
    // and nothing on screen can be clicked again until a reload.
    safeHide() {
      try {
        this.myModal?.hide();
      } catch (e) {
        recordProblem(
          "editor",
          "The dialog could not be closed cleanly",
          e?.message ?? String(e)
        );
      }
      document
        .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
        .forEach((el) => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    },
    isBoxedField(section) {
      return ![
        "featured-products",
        "featured-posts",
        "rich-text",
        "html",
      ].includes(section?.type);
    },
    imageOrientation(section) {
      const hint = `${section?.key ?? ""} ${section?.name ?? ""}`.toLowerCase();
      if (/portrait|phone|mobile|tall/.test(hint)) {
        return "portrait";
      }
      if (/landscape|desktop|wide/.test(hint)) {
        return "landscape";
      }
      return null;
    },
    // Full open sequence — called from mounted() (on-demand v-if mount, the
    // show watcher never fires) and from the show watcher (kept-alive case)
    openModal() {
      // Both of these are otherwise only set by a watcher, and the modal is
      // mounted with v-if once the prop is already in place — so the watcher
      // never fires and they stayed empty: no block name in the header, and a
      // blank language picker.
      this.selectedBlock = this.block ?? {};
      if (this.language === undefined) {
        this.language =
          this.pageLanguage ??
          this.languages?.find((l) => l.default)?.code ??
          this.languages?.[0]?.code;
      }
      // Build settingsData from the prop here as well as in the watcher: the
      // modal mounts with v-if once the prop is already set, so the settings
      // watcher never fires. Without this, settingsData stayed the empty {}
      // from data(), and the loop below then overwrote every translatable
      // field — including the rich text — with a fresh empty object, wiping
      // the content out of the editor.
      this.settingsData = Object.assign({}, this.settings);
      for (const section of this.elements) {
        if (section?.translate) {
          if (typeof this.settingsData[section.key] !== "object") {
            this.settingsData[section.key] = {};
          }
        }
        if (
          section.type === "variable" &&
          typeof this.settingsData[section.key] !== "object"
        ) {
          this.settingsData[section.key] = { type: "variable", val: "" };
        }
      }
      this.productSelector = "";
      this.askClearFilters = false;
      this.filtersWarned = false;
      this.showData = true;
      this.myModal.show();
      this.translatable = false;
      setTimeout(() => {
        this.productList = [{ id: null, products: [] }];
        if (this.selectedBlock?.value?.products) {
          for (const product of this.selectedBlock.value.products) {
            this.productList.push({
              id: product.variant_id,
              products: [product],
            });
          }
        }
        for (const section of this.elements) {
          if (section?.translate) {
            if (typeof this.settingsData[section.key] !== "object") {
              this.settingsData[section.key] = {};
            }
          }
          if (
            section?.type === "featured-products" ||
            section?.type === "featured-posts" ||
            section?.type === "link"
          ) {
            if (this.links.length < 1) {
              http
                .fetch("/products/categories/products/" + this.language)
                .then((data) => {
                  this.addCategories(data["data"]);
                });
              http
                .fetch(
                  "/products/products/characteristics?block_editor=1&language=" +
                    (this.language ?? "en")
                )
                .then((data) => {
                  if (typeof this.settingsData.characteristics !== "object") {
                    this.settingsData.characteristics = {};
                  }
                  for (const characteristic of data) {
                    this.editorMode[characteristic.id] = "select";
                    if (
                      this.settingsData?.var_characteristics?.[
                        characteristic.id
                      ]?.length > 0
                    ) {
                      this.editorMode[characteristic.id] = "variables";
                    }
                    // The productSelector watcher seeds these too, but it runs
                    // before this response arrives and so sees no groups yet.
                    // Without an array here the checkbox v-model drops out of
                    // multi-select mode and one click replaces the whole group
                    // with true, wiping every other tick.
                    if (
                      !Array.isArray(
                        this.settingsData.characteristics[characteristic.id]
                      )
                    ) {
                      this.settingsData.characteristics[characteristic.id] = [];
                    }
                  }
                  this.characteristics = data;
                  this.pinFilterOrder();
                });
              http.fetch("/page").then((data) => {
                for (const item of data.data) {
                  item.target_page_id = item.id;
                  delete item.id;
                  this.links.push(item);
                }
              });
            }
          } else if (section?.type === "rich-text") {
            this.loadQuill(section);
            this.translatable = true;
          } else if (section?.type === "text") {
            this.translatable = true;
          }
        }
      }, 200);
    },
    getValue(section) {
      if (section.translate) {
        if (typeof this.settingsData[section.key] !== "object") {
          this.settingsData[section.key] = {};
        }
        return this.settingsData[section.key][this.language];
      }
      return this.settingsData[section.key];
    },
    setValue(section, newValue) {
      if (section.translate) {
        this.settingsData[section.key][this.language] = newValue;
      } else {
        this.settingsData[section.key] = newValue;
      }
    },
    sortedAndFilteredValues(ch) {
      const term = (this.searchTerms[ch.id] || "")
        .toString()
        .toLowerCase()
        .trim();
      let values = (ch.characteristics_values || []).filter((val) => {
        if (!term) return true;
        return String(val.value).toLowerCase().includes(term);
      });
      return this.pinnedFirst(
        ch.id,
        values,
        this.settingsData.characteristics?.[ch.id]
      );
    },
    // Ticked values first — but by what was ticked when the list was opened,
    // not by what is ticked right now. Sorting on the live selection moved
    // the row out from under the cursor on every click, and the next click
    // then landed on whatever slid into its place.
    pinnedFirst(key, values, selection) {
      const pinned = this.pinnedFilterOrder[key] ?? new Set(selection ?? []);
      const first = values.filter((v) => pinned.has(v.id));
      const rest = values.filter((v) => !pinned.has(v.id));
      return [...first, ...rest];
    },
    // Freeze the current selection as the sort order, so re-opening a block
    // still shows the ticked values on top.
    pinFilterOrder() {
      const pinned = { tags: new Set(this.settingsData.tags ?? []) };
      for (const ch of this.characteristics ?? []) {
        pinned[ch.id] = new Set(
          this.settingsData.characteristics?.[ch.id] ?? []
        );
      }
      this.pinnedFilterOrder = pinned;
    },
    isSelected(chId, valId) {
      return (this.settingsData.characteristics[chId] || []).includes(valId);
    },
    isTagSelected(tagId) {
      return (this.settingsData.tags || []).includes(tagId);
    },
    highlightMatch(text, term) {
      if (!term) return this.escapeHtml(text);
      const regex = new RegExp(`(${this.escapeRegex(term)})`, "ig");
      return this.escapeHtml(text).replace(
        regex,
        '<mark class="bg-warning text-dark rounded px-1">$1</mark>'
      );
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    escapeHtml(unsafe) {
      return String(unsafe)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    setQuillEl(key, el) {
      if (el) {
        this.quillEls[key] = el;
      } else {
        delete this.quillEls[key];
      }
    },
    loadQuill(item) {
      const el = this.quillEls[item.key];
      if (!el) {
        return;
      }
      // eslint-disable-next-line no-undef
      this.quill[item.key] = new Quill(el, {
        theme: "snow",
        placeholder: "Write the text for this block…",
        // A deliberately small set: the editor carries meaning (heading,
        // emphasis, list, link, quote), the theme owns appearance. Colour,
        // font size, alignment, sub/superscript and inline media are left
        // out on purpose — they are the controls that let content override
        // the theme and drift off-brand. The whitelist also strips these on
        // paste, so pasting from Word cannot smuggle them back in.
        // H1 is the page's own title, so headings start at H2.
        formats: ["bold", "italic", "link", "blockquote", "header", "list"],
        modules: {
          toolbar: [
            [{ header: [2, 3, false] }],
            ["bold", "italic"],
            ["link", "blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["clean"],
          ],
        },
      });
    },
    addCategories(categories) {
      for (const item of categories) {
        item.featured_category = item.id;
        delete item.id;
        this.links.push(item);
        this.categories.push(item);
        if (item.categories !== undefined) {
          this.addCategories(item.categories);
        }
      }
    },
    getImageLinks(option) {
      if (option.remote) {
        return {
          src: option.path,
        };
      }

      const types = this.imageSizeTable;

      let sizes = null;
      if (types[option.type] !== undefined) {
        sizes = {};
        for (const size of types[option.type].sizes) {
          sizes[size] =
            this.mediaServer +
            "/" +
            option.path +
            "/" +
            size +
            "-" +
            option.name +
            ".webp";
          // option.format;
        }
      }

      return {
        id: option.id,
        // There is no unsized original on the media server: every file is
        // stored size-prefixed inside the path directory. Pointing src at
        // "<path>/<name>.webp" produced a URL that 404s for every image.
        src: sizes
          ? sizes[types[option.type].sizes[types[option.type].sizes.length - 1]]
          : null,
        format: option.format,
        sizes: sizes,
      };
    },
    getLink(c) {
      if (typeof c === "string") {
        return c;
      }
      if (c.variant_id !== undefined) {
        return "/product/" + c.id + "-" + c.slug + "/" + c.variant_id;
      }
      if (c.target_url !== undefined) {
        return c.target_url;
      }
      if (c.featured_category !== undefined) {
        return "/category/" + c.featured_category + "-" + c.slug;
      }
      if (c.target_page_id !== undefined) {
        return "/" + c.slug;
      }
    },
    changeProducts(variant_id) {
      // The first product picked by hand while the block still runs on
      // filters is the moment the two selection methods start to conflict:
      // warn once, and let the user drop the filter setup or keep it.
      if (variant_id && !this.filtersWarned && this.hasActiveFilters) {
        this.filtersWarned = true;
        this.askClearFilters = true;
      }
      let hasNull = false;
      let count = 0;
      for (const p in this.productList) {
        if (this.productList[p].id === null) {
          if (p !== 0) {
            this.productList.splice(p, 1);
          } else {
            hasNull = true;
          }
        } else if (this.productList[p].id === variant_id) {
          count++;
          if (count > 1) {
            this.productList.splice(p, 1);
          }
        }
      }
      if (!hasNull) {
        this.productList.unshift({ id: null, products: [] });
      }
    },
    confirmClearFilters() {
      for (const key of [
        "featured_category",
        "var_featured_category",
        "featured_bundle_category",
        "var_featured_bundle_category",
        "categories",
        "tags",
        "characteristics",
        "var_characteristics",
        "dynamic_block",
        "discount",
        "limit",
        "target_sort",
        "showSoldOut",
        "types",
      ]) {
        delete this.settingsData[key];
      }
      this.askClearFilters = false;
    },
    onLinkSearch(search, k) {
      this.linkSearch = search;
      this.fetchProducts(search, k, this.linkProducts);
    },
    fetchProducts(search, loading, p) {
      if (search.length < 3) {
        return;
      }
      this.q = search;
      loading(true);
      http
        .fetch(
          this.productSelector === "bundle"
            ? "/bundles/search"
            : "/products/products/search",
          {
            q: search,
            all: 1,
          }
        )
        .then((data) => {
          p.products = data;
          loading(false);
        })
        .catch(() => {
          loading(false);
        });
    },
    fetchImages(search, loading, name) {
      if (search.length < 3) {
        return;
      }
      loading(true);
      http
        .fetch("/images/search", {
          q: search,
        })
        .then((data) => {
          this.images[name] = data;
          loading(false);
        })
        .catch(() => {
          loading(false);
        });
    },
    browseImages(name, imageType = 3) {
      http.fetch("/images?type=" + imageType).then((data) => {
        this.images[name] = data;
      });
    },
    deleteImage(sectionKey, imgId) {
      this.images[sectionKey] = this.images[sectionKey].filter(
        (img) => img.id !== imgId
      );
      http
        .fetch("/images/delete", {
          image: imgId,
        })
        .then((data) => {
          if (data.need_delete) {
            const request = new XMLHttpRequest();
            const formData = new FormData();
            formData.append("token", data.token);
            formData.append("path", data.image.path);

            request.open("POST", tenant.getAppMediaApi() + "/images/delete");

            request.send(formData);
          }
        });
    },
    save() {
      let block = this.selectedBlock;
      for (const section of this.elements) {
        if (
          section?.type === "featured-products" ||
          section?.type === "featured-posts"
        ) {
          if (this.productSelector === "filter") {
            // The two lists are saved as picked. The merged categories list
            // that an earlier version of the editor wrote is dropped.
            delete this.settingsData.categories;
          }
          if (
            this.productSelector === "product" ||
            this.productSelector === "bundle"
          ) {
            this.settingsData.featured_category = [];
            this.settingsData.characteristics = null;
            this.settingsData.var_characteristics = null;
            delete this.settingsData.featured_category;
            delete this.settingsData.featured_bundle_category;
            delete this.settingsData.types;
            delete this.settingsData.categories;
            delete this.settingsData.characteristics;
            delete this.settingsData.var_characteristics;
            delete this.settingsData.tags;
            this.settingsData.featured_products = [];
            this.settingsData.featured_bundles = [];
            block.value.products = [];
            for (const productItem of this.productList) {
              if (productItem.id === null || productItem.id === undefined) {
                continue;
              }
              let product = productItem.products.find(
                (e) => e.variant_id === productItem.id
              );
              if (product.type === "BUNDLE") {
                this.settingsData.featured_bundles.push(productItem.id);
              } else {
                this.settingsData.featured_products.push(productItem.id);
              }
              block.value.products.push(product);
            }
          } else {
            this.settingsData.featured_products = [];
            delete this.settingsData.featured_products;
            this.settingsData.featured_bundles = [];
            delete this.settingsData.featured_bundles;
            block.value.products = [];
          }
        } else if (section.type === "rich-text") {
          // Only overwrite from the editor if it actually mounted; otherwise a
          // failed init would blank the saved text instead of leaving it be.
          const q = this.quill[section.key];
          if (q) {
            if (section?.translate) {
              this.settingsData[section.key][this.language] = q.root.innerHTML;
            } else {
              this.settingsData[section.key] = q.root.innerHTML;
            }
          }
        }
      }

      this.$emit("update:block", this.selectedBlock);
      //   this.$emit("update:settings", this.settingsData);
      this.$emit("save", this.settingsData);
      this.$emit("update:show", false);
    },
    startUpload(id) {
      document.getElementById("file_" + id).click();
      // this.$refs["file_" + i].click();
    },
    btoa(str) {
      return window.btoa(str);
    },
    upload(key, type) {
      const input = document.getElementById("file_" + window.btoa(key));
      const file = input?.files?.[0];
      if (file) {
        this.uploadFile(file, key, type);
      }
      // let the same file be picked twice in a row
      if (input) {
        input.value = "";
      }
    },
    // The backend and the media server identify image types by string key
    // ("cover", "product", …) while the block definitions and the library
    // listing still speak numeric ids. Translate before either API sees it.
    imageTypeKey(type) {
      const keys = {
        0: "product",
        2: "category",
        3: "cover",
        4: "color",
        5: "icon",
        6: "asset",
      };
      const index =
        typeof type === "string" && /^\d+$/.test(type) ? Number(type) : type;
      return keys[index] ?? (typeof index === "string" ? index : "cover");
    },
    // Takes a File rather than reading one out of an input, so a dropped file
    // and a picked file travel the same path.
    async uploadFile(file, key, type) {
      const maxSize = 120 * 1024 * 1024; // 120 MB in bytes

      if (!file) {
        return;
      }
      if (!/^image\//.test(file.type)) {
        this.uploadError = "That file is not an image.";
        return;
      }
      if (file.size > maxSize) {
        this.uploadError = "That image is larger than 120 MB.";
        return;
      }
      this.uploadError = "";

      // Downscale huge images before upload, same as core-frontend.
      try {
        const bitmap = await createImageBitmap(file);
        const maxWidth = 3840;
        if (bitmap.width >= maxWidth) {
          const scale = Math.min(1, maxWidth / bitmap.width);

          const width = Math.round(bitmap.width * scale);
          const height = Math.round(bitmap.height * scale);

          const fromCanvas = document.createElement("canvas");
          fromCanvas.width = bitmap.width;
          fromCanvas.height = bitmap.height;

          const fromCtx = fromCanvas.getContext("2d");
          fromCtx.drawImage(bitmap, 0, 0);

          const toCanvas = document.createElement("canvas");
          toCanvas.width = width;
          toCanvas.height = height;
          const mimeType = file.type || "image/jpeg";

          // High-quality resize
          await picaInstance.resize(fromCanvas, toCanvas);

          // Export compressed image
          const resizedBlob = await picaInstance.toBlob(
            toCanvas,
            mimeType,
            0.96
          );

          // Preserve filename
          file = new File([resizedBlob], file.name, {
            type: mimeType,
          });
        }
      } catch (err) {
        console.error(err);
        this.uploadProccess = false;
        this.uploadError = "Image resizing failed.";
        return;
      }

      const typeKey = this.imageTypeKey(type);
      const request = new XMLHttpRequest();
      this.uploadPercent = 0;
      this.uploadProccess = true;
      const id = Math.round(new Date() / 1000);
      const fileName = file.name.replace(/ /g, "-");
      let send = { type: typeKey, id: id, images: [fileName] };

      const _this = this;
      http
        .fetch("/files/upload", send)
        .then((data) => {
          const formData = new FormData();
          formData.append("image", file, fileName);
          formData.append("token", data.token);
          formData.append("album", id);
          formData.append("category", typeKey);

          request.open("POST", tenant.getAppMediaApi() + "/images/upload");

          request.upload.onprogress = function (evt) {
            let e = Math.ceil((evt.loaded / file.size) * 60);
            _this.uploadPercent = e;
            if (e > 59) {
              let interval = setInterval(() => {
                if (_this.uploadProccess && _this.uploadPercent < 98) {
                  _this.uploadPercent += 1;
                } else {
                  clearInterval(interval);
                }
              }, 2000);
            }
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              let data2 = JSON.parse(request.responseText);
              const uploaded = data2.operation?.images?.[0];
              _this.uploadPercent = 98;
              setTimeout(() => {
                _this.uploadPercent = 100;
                _this.uploadProccess = false;
                if (!uploaded) {
                  _this.uploadError = "The server did not return the image.";
                  return;
                }
                _this.settingsData[key] = _this.getImageLinks(uploaded);
                // The dialog used to close on upload, which is why a new file
                // looked like it had gone nowhere. It stays open, with the file
                // first in the grid and marked as the one in the field.
                if (_this.picker.open) {
                  _this.pickerPinned = [uploaded, ..._this.pickerPinned];
                  _this.pickerNotice = `${uploaded.name} uploaded — it is first in the list and now in the field.`;
                  _this.loadPickerImages(true);
                }
              }, 200);
            } else {
              _this.uploadProccess = false;
              _this.uploadError = "Upload failed. Please try again.";
            }
          };

          request.onerror = function () {
            _this.uploadProccess = false;
            _this.uploadError = "Upload failed. Please check your connection.";
          };

          request.send(formData);
        })
        .catch((err) => {
          // The rejection used to be dropped on the floor, so a refused
          // upload looked like a stalled one. The server's own words are
          // carried through: they say which field it disliked.
          _this.uploadProccess = false;
          const detail = err?.message ? ` (${err.message})` : "";
          _this.uploadError = `The server refused the upload${detail}.`;
        });
    },
  },
};
</script>

<style scoped>
.modal-body h3 {
  font-size: 15px;
  font-weight: 700;
  color: #16181d;
  margin: 4px 0 10px;
}
.soldout-row {
  max-width: 220px;
  margin-top: 10px;
}
.hover-shadow {
  transition: box-shadow 0.2s;
}
.hover-shadow:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}
.form-check {
  cursor: pointer;
}
mark {
  background-color: #fff3bf;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}
.option-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.option-card:hover {
  border-color: #c4b5fd;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(124, 58, 237, 0.12);
}
.option-card.active {
  border-color: #7c3aed;
  background: rgba(124, 58, 237, 0.06);
  box-shadow: 0 0 0 1px #7c3aed;
}
.option-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
  display: grid;
  place-items: center;
  margin-bottom: 6px;
}
.option-card.active .option-card-icon {
  background: #7c3aed;
  color: #fff;
}
.option-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #16181d;
}
.option-card-sub {
  font-size: 12px;
  color: #768692;
  font-weight: 500;
}

/* ---- Boxed field: the name lives inside the control's own frame ----
   Bootstrap's row/col wrappers differ between field types (the link picker
   sits in .col-sm-12, the plain inputs do not), which is what made the
   controls end at different x. Everything inside the box is reset to one
   width so the type of control can no longer change the geometry. */
.field-box {
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #fff;
  padding: 18px 12px 5px;
  margin-bottom: 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.field-box:focus-within {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
/* Floating label. Empty: the name sits large and faint over the input, so the
   box reads as already showing what belongs there. On focus or once filled it
   shrinks to the top — unlike a plain placeholder it never disappears, so a
   half-filled form still says what each value is. */
.field-box-label {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: #cbd5e1;
  letter-spacing: 0;
  pointer-events: none;
  transition: top 0.14s ease, font-size 0.14s ease, color 0.14s ease,
    transform 0.14s ease;
}
/* A field the block list counts as a SEO gap says so on its own label, so the
   badge in the list has somewhere to lead. The label itself ignores the
   mouse — this one has to take it back, or its tooltip never opens. */
.seo-flag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 5px;
  background: #fef3c7;
  color: #92400e;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  vertical-align: middle;
  pointer-events: auto;
  cursor: help;
}
.field-box:focus-within .field-box-label,
.field-box.is-filled .field-box-label {
  top: 5px;
  transform: none;
  font-size: 10.5px;
  line-height: 12px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.2px;
}
/* Every control resolves to the same 20px row, so a box's height no longer
   depends on which control type happens to sit in it. */
.field-box :deep(input),
.field-box :deep(select),
.field-box :deep(.form-control),
.field-box :deep(.vs__search),
.field-box :deep(.vs__selected) {
  line-height: 20px;
}
.field-box :deep(.vs__dropdown-toggle),
.field-box :deep(.vs__actions) {
  height: 20px;
  align-items: center;
}
.field-box :deep(.vs__actions svg) {
  display: block;
}
.field-box :deep(.row),
.field-box :deep([class^="col-"]),
.field-box :deep([class*=" col-"]) {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  flex: none;
}
.field-box :deep(.mb-4),
.field-box :deep(.mb-3),
.field-box :deep(.input-group) {
  margin-bottom: 0 !important;
}
.field-box :deep(input),
.field-box :deep(select),
.field-box :deep(textarea),
.field-box :deep(.form-control) {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  height: auto;
  min-height: 0;
  font-size: 14px;
  color: #1e293b;
  box-shadow: none;
  outline: none;
  border-radius: 0;
}
.field-box :deep(textarea) {
  min-height: 62px;
  resize: vertical;
}
/* vue-select renders its own bordered frame */
.field-box :deep(.v-select) {
  width: 100%;
}
.field-box :deep(.vs__dropdown-toggle) {
  border: 0;
  padding: 0;
  min-height: 0;
  background: transparent;
}
.field-box :deep(.vs__selected-options) {
  padding: 0;
}
.field-box :deep(.vs__search),
.field-box :deep(.vs__search:focus),
.field-box :deep(.vs__selected) {
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 1.5;
}
.field-box :deep(.vs__actions) {
  padding: 0;
}

/* ---- Two-column body: text on the left, images beside it ---- */
.edit-grid {
  display: grid;
  gap: 28px;
  align-items: start;
}
/* The modal has a fixed width, so the text column can only gain what the
   image column and the gap give up. 320px is the narrowest the stacked
   image field renders cleanly at. */
.edit-grid.has-side {
  grid-template-columns: minmax(0, 1fr) 320px;
}
.edit-side {
  display: flex;
  flex-direction: column;
  gap: 26px;
}
/* The side column is too narrow for the label/preview/buttons row used at
   full width, so the image field stacks here instead. */
.edit-side .img-row,
.edit-side .img-field {
  flex-direction: column;
  gap: 10px;
}
.edit-side .img-label {
  flex: none;
  width: auto;
  padding-top: 0;
}
.edit-side .img-drop {
  height: 120px;
}
@media (max-width: 1100px) {
  .edit-grid.has-side {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ---- Image field label row ---- */
/* ---- Image field ---- */
.img-row {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 4px;
}
/* Narrow label column: long field names wrap here instead of running the
   full modal width and pushing the picker down. */
.img-label {
  flex: 0 0 148px;
  width: 148px;
  display: flex;
  gap: 8px;
  padding-top: 4px;
}
.img-label-icon {
  color: #7c3aed;
  flex: none;
  line-height: 0;
  margin-top: 1px;
}
.img-label-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.img-label-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
}
.img-label-hint {
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.35;
}

/* ---- Image field: the field itself is the drop target ---- */
.img-field {
  position: relative;
}
.img-drop {
  height: 150px;
  flex: none;
  aspect-ratio: 3 / 4;
  border: 1.5px dashed #cbd5e1;
  border-radius: 10px;
  background-color: #f8fafc;
  padding: 0;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: border-color 0.15s, background 0.15s;
}
.img-drop.is-portrait {
  aspect-ratio: 2 / 3;
}
.img-drop.is-landscape {
  aspect-ratio: 3 / 2;
}
.img-drop.is-empty {
  background-image: linear-gradient(
      to top right,
      transparent calc(50% - 1px),
      #e5e9f0 calc(50% - 1px),
      #e5e9f0 calc(50% + 1px),
      transparent calc(50% + 1px)
    ),
    linear-gradient(
      to bottom right,
      transparent calc(50% - 1px),
      #e5e9f0 calc(50% - 1px),
      #e5e9f0 calc(50% + 1px),
      transparent calc(50% + 1px)
    );
}
.img-drop:hover,
.img-drop.is-dragging {
  border-color: #7c3aed;
  background-color: rgba(124, 58, 237, 0.06);
}
.img-drop img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  grid-area: 1 / 1;
}
.img-drop-hint {
  grid-area: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #94a3b8;
  pointer-events: none;
  padding: 6px;
  text-align: center;
}
/* over a filled field the hint is the hover affordance, not the resting state */
.img-drop:not(.is-empty) .img-drop-hint {
  opacity: 0;
  background: rgba(255, 255, 255, 0.88);
  width: 100%;
  height: 100%;
  justify-content: center;
  transition: opacity 0.15s;
}
.img-drop:not(.is-empty):hover .img-drop-hint,
.img-drop.is-dragging .img-drop-hint {
  opacity: 1;
}
.img-drop-title {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
/* the tall field is only 80px wide in the side column, so the second line
   goes and the first is sized to fit inside it */
.img-drop.is-portrait .img-drop-sub {
  display: none;
}
.img-drop.is-portrait .img-drop-hint {
  padding: 3px;
}
.img-drop.is-portrait .img-drop-title {
  font-size: 10px;
}
.img-drop-sub {
  font-size: 10.5px;
  font-weight: 600;
  color: #cbd5e1;
}
.img-clear {
  position: absolute;
  top: 6px;
  right: 6px;
  border: 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 5px rgba(18, 18, 18, 0.18);
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
}
.img-clear:hover {
  background: #fef2f2;
  color: #c53030;
}

/* ---- Media picker ---- */
.picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.5);
  display: grid;
  place-items: center;
  padding: 20px;
}
.picker-panel {
  position: relative;
  width: min(1140px, 100%);
  max-height: min(880px, 92vh);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
  overflow: hidden;
}
.picker-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid #eef2f7;
}
.picker-head h5 {
  margin: 0;
  font-size: 15.5px;
  font-weight: 700;
  color: #1e293b;
}
.picker-count {
  font-size: 12.5px;
  font-weight: 600;
  color: #94a3b8;
}
.picker-close {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  line-height: 0;
  padding: 5px;
  border-radius: 7px;
}
.picker-close:hover {
  background: #f1f5f9;
  color: #475569;
}
.picker-tools {
  display: flex;
  gap: 10px;
  padding: 14px 20px 0;
}
.picker-searchwrap {
  position: relative;
  flex: 1;
}
.picker-searchwrap svg {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}
.picker-search {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 9px 12px 9px 34px;
  font-size: 14px;
  outline: none;
}
.picker-search:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
.picker-upload {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 9px;
  background: #7c3aed;
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  padding: 0 16px;
  cursor: pointer;
  white-space: nowrap;
}
.picker-upload:hover {
  background: #6d28d9;
}
.picker-hint {
  margin: 8px 20px 0;
  font-size: 12px;
  font-weight: 500;
  color: #a0aec0;
}
.picker-scan {
  margin: 4px 20px 0;
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
}
.picker-scan.is-warn {
  color: #b45309;
}
.picker-note {
  margin: 10px 20px 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: #ecfdf5;
  color: #047857;
  font-size: 12.5px;
  font-weight: 600;
}
.picker-error {
  margin: 10px 20px 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12.5px;
  font-weight: 600;
}
.picker-progress {
  margin: 10px 20px 0;
  height: 4px;
  border-radius: 3px;
  background: #eef2f7;
  overflow: hidden;
}
.picker-progress span {
  display: block;
  height: 100%;
  background: #7c3aed;
  transition: width 0.2s;
}
.picker-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 20px 20px;
}
.picker-loading,
.picker-none {
  text-align: center;
  padding: 30px 0;
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 14px;
}
.picker-card {
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.picker-card:hover {
  border-color: #c4b5fd;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.1);
}
.picker-card.is-current {
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.18);
}
.picker-pick {
  display: block;
  width: 100%;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: #f8fafc;
}
/* The frame holds the shape and the picture sits inside it whole — cropping
   thumbnails to a tile made near-identical shots indistinguishable. */
.picker-card img {
  display: block;
  width: 100%;
  height: 158px;
  object-fit: contain;
  padding: 6px;
  box-sizing: border-box;
}
.picker-card-foot {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 9px;
  border-top: 1px solid #f1f5f9;
}
.picker-card-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Says why there is no trash icon on this one. */
.picker-card-used {
  flex: none;
  padding: 2px 7px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10.5px;
  font-weight: 700;
  cursor: help;
}
.picker-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 8px;
  border-radius: 20px;
  background: #7c3aed;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
}
.picker-acts {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.15s;
}
.picker-card:hover .picker-acts,
.picker-card:focus-within .picker-acts {
  opacity: 1;
}
.picker-act {
  border: 0;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.62);
  color: #fff;
}
.picker-act:hover {
  background: rgba(15, 23, 42, 0.85);
}
.picker-act.is-danger:hover {
  background: #dc2626;
}
.picker-more {
  padding: 18px 0 4px;
  text-align: center;
}
.picker-more button {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 9px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.picker-more button:hover {
  border-color: #7c3aed;
  color: #5b21b6;
}
.picker-dropmask {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: grid;
  place-items: center;
  background: rgba(124, 58, 237, 0.12);
  border: 2px dashed #7c3aed;
  border-radius: 16px;
  color: #5b21b6;
  font-size: 15px;
  font-weight: 700;
}
.picker-ask {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(15, 23, 42, 0.35);
  display: grid;
  place-items: center;
  padding: 20px;
}
.picker-ask-card {
  width: min(430px, 100%);
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.3);
}
.picker-ask-card h6 {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.picker-ask-card p {
  margin: 0 0 6px;
  font-size: 13px;
  color: #475569;
}
.picker-ask-warn {
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-weight: 600;
}
.picker-ask-list {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: #475569;
}
.picker-ask-acts {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.picker-ask-acts button {
  border-radius: 9px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
}
.picker-ask-acts button:disabled {
  opacity: 0.6;
  cursor: default;
}
.picker-ask-acts .is-danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}
.filter-ask {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.35);
  display: grid;
  place-items: center;
  padding: 20px;
}
.picker-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.82);
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 12px;
  padding: 18px;
}
.picker-lightbox-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
}
.picker-lightbox-bar button {
  margin-left: auto;
  border: 0;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border-radius: 8px;
  padding: 6px 12px;
  font-weight: 600;
  cursor: pointer;
}
.picker-lightbox img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  margin: auto;
  object-fit: contain;
}

/* ---- Rich text field ----
   Quill's own "snow" theme was left unstyled: two stacked bare borders and a
   one-line editing area. Toolbar and editor are framed as a single control
   here, with a writing area big enough to actually write in.
   Quill builds its toolbar at runtime, so these need :deep() — scoped
   attributes are only stamped on markup that exists at compile time. */
.rt-field {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rt-field:focus-within {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
.rt-field :deep(.ql-toolbar.ql-snow) {
  border: 0;
  border-bottom: 1px solid #eef2f7;
  background: #f8fafc;
  padding: 6px 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  font-family: inherit;
}
/* a hairline between groups instead of Quill's undifferentiated run */
.rt-field :deep(.ql-toolbar.ql-snow .ql-formats) {
  margin: 0 6px 0 0;
  padding-right: 6px;
  border-right: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}
.rt-field :deep(.ql-toolbar.ql-snow .ql-formats:last-child) {
  border-right: 0;
  padding-right: 0;
  margin-right: 0;
}
.rt-field :deep(.ql-toolbar.ql-snow button) {
  width: 27px;
  height: 27px;
  padding: 4px;
  border-radius: 6px;
  transition: background 0.12s;
}
.rt-field :deep(.ql-toolbar.ql-snow button:hover),
.rt-field :deep(.ql-toolbar.ql-snow .ql-picker-label:hover) {
  background: rgba(124, 58, 237, 0.1);
}
.rt-field :deep(.ql-snow.ql-toolbar button.ql-active) {
  background: #7c3aed;
}
/* Quill draws its icons as strokes and fills, not as font glyphs */
.rt-field :deep(.ql-snow .ql-stroke) {
  stroke: #475569;
}
.rt-field :deep(.ql-snow .ql-fill) {
  fill: #475569;
}
.rt-field :deep(.ql-snow.ql-toolbar button:hover .ql-stroke),
.rt-field :deep(.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke) {
  stroke: #5b21b6;
}
.rt-field :deep(.ql-snow.ql-toolbar button:hover .ql-fill) {
  fill: #5b21b6;
}
.rt-field :deep(.ql-snow.ql-toolbar button.ql-active .ql-stroke) {
  stroke: #fff;
}
.rt-field :deep(.ql-snow.ql-toolbar button.ql-active .ql-fill) {
  fill: #fff;
}
.rt-field :deep(.ql-snow .ql-picker) {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}
.rt-field :deep(.ql-snow .ql-picker-label) {
  border: 0;
  border-radius: 6px;
  padding-left: 8px;
}
.rt-field :deep(.ql-snow .ql-picker-options) {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  box-shadow: 0 12px 30px rgba(18, 18, 18, 0.12);
  padding: 5px;
}
.rt-field :deep(.ql-snow .ql-picker-item) {
  border-radius: 6px;
  padding: 5px 8px;
}
.rt-field :deep(.ql-snow .ql-picker-item:hover) {
  background: rgba(124, 58, 237, 0.1);
  color: #5b21b6;
}
/* the writing area */
/* Quill ships .ql-container { height: 100% }, which resolves against whatever
   the surrounding layout happens to be — in this modal that came out only a
   few pixels tall, which is why the field looked like a single line. Sizing to
   content with a floor and a ceiling is what makes it behave like an editor. */
.rt-field :deep(.ql-container.ql-snow) {
  border: 0;
  height: auto;
  font-family: inherit;
}
.rt-field :deep(.ql-editor) {
  /* .ql-editor carries height:100% as well — both it and the container have
     to size to content, or the field pins itself to whatever bound it can
     find (a few pixels here, the whole viewport there). */
  height: auto;
  min-height: 210px;
  max-height: 420px;
  overflow-y: auto;
  padding: 16px 18px;
  font-size: 15px;
  line-height: 1.65;
  color: #1e293b;
}
.rt-field :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #cbd5e1;
  left: 18px;
  right: 18px;
}
/* headings sized so the editor previews the shape of the finished text */
.rt-field :deep(.ql-editor h1) {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 8px;
}
.rt-field :deep(.ql-editor h2) {
  font-size: 21px;
  font-weight: 700;
  margin: 0 0 7px;
}
.rt-field :deep(.ql-editor h3) {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 6px;
}
.rt-field :deep(.ql-editor p) {
  margin: 0 0 9px;
}
.rt-field :deep(.ql-editor blockquote) {
  border-left: 3px solid #c4b5fd;
  margin: 0 0 9px;
  padding: 2px 0 2px 14px;
  color: #475569;
}
.rt-field :deep(.ql-editor a) {
  color: #7c3aed;
}
.rt-field :deep(.ql-snow .ql-tooltip) {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  box-shadow: 0 12px 30px rgba(18, 18, 18, 0.14);
}

/* ---- Product search results ---- */
.prod-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.prod-opt-img,
.picked-img {
  width: 34px;
  height: 45px;
  flex: none;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
  display: block;
}
.prod-opt-img.is-empty,
.picked-img.is-empty {
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
}
.prod-opt-text,
.picked-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.prod-opt-name,
.picked-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.prod-opt-meta,
.picked-meta {
  display: flex;
  gap: 8px;
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 600;
}

/* ---- Chosen products ---- */
.picked-list {
  margin-bottom: 16px;
}
.picked-hint {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
  text-transform: none;
  letter-spacing: 0;
}
.picked-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  margin-bottom: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #fff;
}
.picked-grip {
  color: #cbd5e1;
  cursor: grab;
  line-height: 0;
  flex: none;
}
.picked-grip:active {
  cursor: grabbing;
}
.picked-row .picked-text {
  flex: 1;
}
.picked-remove {
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 6px;
  padding: 4px;
  line-height: 0;
  flex: none;
}
.picked-remove:hover {
  background: #fef2f2;
  color: #c53030;
}

/* ---- Block identity in the header ---- */
.modal-block-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: none;
  margin-right: 11px;
  border-radius: 9px;
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
}
.modal-block-icon :deep(svg) {
  width: 19px;
  height: 19px;
  display: block;
}
.modal-title-stack {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.modal-title-stack .modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.25;
}
.modal-title-type {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  line-height: 1.3;
}

/* ---- Link picker dropdown ---- */
.field-box :deep(.vs__dropdown-menu) {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 34px rgba(18, 18, 18, 0.12);
  padding: 5px;
  margin-top: 9px;
  max-height: 280px;
}
.field-box :deep(.vs__dropdown-option) {
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 13.5px;
  color: #334155;
}
.field-box :deep(.vs__dropdown-option--highlight) {
  background: rgba(124, 58, 237, 0.1);
  color: #5b21b6;
}
.field-box :deep(.vs__dropdown-option--selected) {
  font-weight: 700;
}
.field-box :deep(.vs__no-options) {
  font-size: 12.5px;
  color: #94a3b8;
  padding: 10px;
}
/* the theme's own badges inside an option */
.field-box :deep(.vs__dropdown-option .badge) {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 5px;
}

/* ---- Language picker in the header ---- */
.lang-pick {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-left: auto;
  margin-right: 14px;
  padding: 5px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #7c3aed;
}
.lang-pick-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
.lang-pick select {
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  outline: none;
  padding-right: 2px;
}

/* ---- Filter panel ---- */
.filter-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.filter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
  align-items: start;
}
/* number inputs aren't covered by the global .field rule */
.filter-panel .field input[type="number"] {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  outline-color: #7c3aed;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  font-weight: 500;
  color: #94a3b8;
}
.filter-row-2 {
  display: grid;
  grid-template-columns: 1.4fr 1fr auto;
  gap: 16px;
  align-items: end;
}
.filter-row-2 .soldout-row {
  align-self: center;
  margin-bottom: 6px;
  white-space: nowrap;
}
.mode-switch {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.filter-groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}
.filter-group {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.filter-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-group-title {
  font-size: 13px;
  font-weight: 700;
  color: #16181d;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.filter-count {
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
  border-radius: 20px;
  padding: 1px 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.filter-clear {
  border: none;
  background: transparent;
  color: #7c3aed;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.filter-search {
  flex: none;
  width: 120px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 600;
  background: #fff;
  outline-color: #7c3aed;
}
.filter-check-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-height: 200px;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 0 4px;
}
.filter-check {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: background 0.12s;
}
.filter-check:hover {
  background: #f1f5f9;
}
.filter-check.selected {
  background: rgba(124, 58, 237, 0.08);
  color: #5b21b6;
}
.filter-check input[type="checkbox"] {
  width: 15px;
  height: 15px;
  flex: none;
  accent-color: #7c3aed;
}
.filter-empty {
  font-size: 12px;
  color: #94a3b8;
  padding: 6px 8px;
}

.segmented-control {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  background: #f1f5f9;
  padding: 2px;
  font-family: inherit;
  -webkit-font-smoothing: antialiased;
}

/* hide radio inputs but keep them focusable */
.segmented-control input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

/* labels act as buttons */
.segmented-control label {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
  color: #475569;
  border-radius: 999px;
  transition: background 0.15s ease, color 0.15s ease;
  user-select: none;
  min-width: 96px;
  text-align: center;
}

/* selected state using adjacent-sibling selector */
.segmented-control input[type="radio"]:checked + label {
  background: #7c3aed;
  color: #fff;
  font-weight: 700;
}

/* focus visible for keyboard users */
.segmented-control input[type="radio"]:focus + label {
  outline: 3px solid rgba(124, 58, 237, 0.2);
  outline-offset: 2px;
  z-index: 1;
}

/* small modifier to fit tight spaces */
.segmented-control--sm label {
  padding: 4px 8px;
  font-size: 0.8rem;
  min-width: 88px;
}

/* responsive: collapse to stacked small buttons on tiny screens */
@media (max-width: 420px) {
  .segmented-control {
    flex-direction: column;
    border-radius: 8px;
  }
  .segmented-control label {
    min-width: 100%;
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
  }
  .segmented-control label:first-child {
    border-top: none;
  }
}
</style>
